import { DeliveryModel } from '@audibene/ta.utils-opportunity'
import { Button } from '@mui/material'
import { styled } from '@mui/material/styles'
import { CSSProperties, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Country, TechSetupFailureReason, techSetupReasonDisplayText } from '../../../../../constants'
import { useAppointment, useCountry, useFeatureFlags, useIsFitterSchedulingMode } from 'hooks'
import { AppointmentRescheduleFormData } from '../../../../../types'
import { sortByName } from '../../../../../util'
import { AppointmentInfo } from '../../../../AppointmentInfo'
import { CustomerName } from '../../../../CustomerName'
import { Loader, Container as LoaderContainer } from '../../../../Loader'
import { Spacer } from '../../../../Spacer'
import { Subtitle } from '../../../../Subtitle'
import { Title } from '../../../../Title'
import { Select as FormSelect, TextArea } from '../../../../forms'
import { CallToActions } from '../../../../schedule'
import { getPrimaryRescheduleReasonDisplayText, getPrimaryRescheduleReasons, PrimaryAppointmentRescheduleReason } from '../../utils'
import { useTranslation } from 'react-i18next'

const Select = styled(FormSelect)`
  max-width: 50%;
`

const sortByValueWithOtherLast = (a: { value: string }, b: { value: string }): number => {
  if (a.value === PrimaryAppointmentRescheduleReason.OTHER) return 1
  if (b.value === PrimaryAppointmentRescheduleReason.OTHER) return -1

  return a.value.localeCompare(b.value)
}

const techSetupFailureReasons = Object.values(TechSetupFailureReason)
  .map((reason) => ({
    name: techSetupReasonDisplayText[reason],
    value: reason,
  }))
  .sort(sortByName)

interface ContentProps {
  deliveryModel?: DeliveryModel
  isPooledSchedulingOn: boolean | undefined
}

const Content = ({ deliveryModel, isPooledSchedulingOn }: ContentProps) => {
  const { t } = useTranslation()
  const reason = useWatch<AppointmentRescheduleFormData>({
    name: 'reason',
  })
  const country = useCountry()

  const { formState } = useFormContext<AppointmentRescheduleFormData>()
  const { isLoading: isAppointmentLoading } = useAppointment()
  const isFitterSchedulingMode = useIsFitterSchedulingMode()

  const [isTechSetupFailReasonsEnabledFeatureFlag] = useFeatureFlags<boolean>(['DISPLAY_TECH_SETUP_REASONS'])

  const reasons = useMemo(() => {
    const displayText = getPrimaryRescheduleReasonDisplayText(country, deliveryModel)
    const values = getPrimaryRescheduleReasons(country, deliveryModel, isPooledSchedulingOn)
    let result = values.map((value) => ({ name: displayText[value], value }))
    if (country !== Country.DE) {
      result = result.toSorted(sortByValueWithOtherLast)
      if (!isTechSetupFailReasonsEnabledFeatureFlag) {
        result = result.filter((opt) => opt.value !== PrimaryAppointmentRescheduleReason.TECH_SETUP_FAILED)
      }
    }
    return result
  }, [country, deliveryModel, isTechSetupFailReasonsEnabledFeatureFlag, isPooledSchedulingOn])

  const { isSubmitting } = formState
  const isLoading = isAppointmentLoading || isSubmitting
  const isNoteRequired = reason === PrimaryAppointmentRescheduleReason.OTHER
  const isPrimaryReasonTechSetup = reason === PrimaryAppointmentRescheduleReason.TECH_SETUP_FAILED
  const notePlaceholderText = isNoteRequired ? t('common.message.providerMoreInformation') : t('common.message.optionalMoreInformation')

  const ctaHeight = '3.5rem'

  const style: CSSProperties = isFitterSchedulingMode
    ? {
        alignItems: 'center',
        background: '#fff',
        bottom: 0,
        display: 'grid',
        height: ctaHeight,
        left: 0,
        position: 'fixed',
        right: 0,
      }
    : { width: '100%' }

  return (
    <>
      {isLoading && (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      )}
      {!isFitterSchedulingMode && (
        <>
          <Title>{t('pages.appointment.reschedule.title')}</Title>
          <Spacer count={0.5} />
        </>
      )}
      <Subtitle>{t('pages.appointment.reschedule.subtitle')}</Subtitle>
      <Spacer />
      <CustomerName />
      <Spacer count={0.25} />
      <AppointmentInfo />
      <Spacer count={1.5} />
      <Select defaultValue="" disabled={isLoading} label={t('pages.appointment.reschedule.reason')} name="reason" options={reasons} />
      {isPrimaryReasonTechSetup && (
        <>
          <br />
          <Select
            defaultValue=""
            disabled={isLoading}
            label={t('common.label.reasonForUnsuccessfulTechSetup')}
            name="techSetupFailReason"
            options={techSetupFailureReasons}
          />
        </>
      )}
      <Spacer count={2.5} />
      {country !== Country.DE && (
        <TextArea
          disabled={isLoading}
          fullWidth
          InputLabelProps={{ shrink: true }}
          label={t('common.label.additionalInformation')}
          multiline
          name="description"
          placeholder={notePlaceholderText}
          rows={4}
          variant="outlined"
        />
      )}
      <Spacer count={2} />
      {isFitterSchedulingMode ? <div style={{ height: ctaHeight }} /> : <div style={{ height: '0.5rem' }} />}
      <div style={style}>
        <CallToActions>
          <Button color="primary" disabled={isLoading} type="submit" variant="contained">
            {t('common.next')}
          </Button>
        </CallToActions>
      </div>
    </>
  )
}

export { Content }
